import { defineField, defineGroup } from '@/form/useForm'
import { EAssetSource, EAssetType, type IProviderImport, type IValue } from 'findef'
import { lockedByIr, lockedByProvider } from '../toolips'
import { getCurrencyOptions } from '@/data/currencies'
import { useUserSettings } from '@/modules/settings/hooks/useUserSettings'
import type { UseAssetFormComputedData, UseAssetFormState } from '../types'
import { prettyAssetType } from '@/utils/asset'
import { match } from '@/utils/etc'

const CURRENCIES = getCurrencyOptions()

export const investedField = () => {
  const settings = useUserSettings()
  return defineGroup<UseAssetFormState, IValue, UseAssetFormComputedData>({
    attributes: {
      class: 'grid grid-cols-[auto_7rem]',
      label: 'Total Invested',
			needs: ['asset.type'],
      next: (state) => {
        const tooltip = (() => {
          switch (state.asset.type) {
            case EAssetType.REAL_ESTATE:
              return 'The total amount that the property was purchased for including e.g. downpayment & loan financing';
            case EAssetType.CASH:
              return 'The initial amount of cash deposited before accumulating any interest';
            default:
              return `The total amount invested in this ${prettyAssetType(state.asset.type, false, undefined, false, false)} asset`;
          }
        })();
        const label = match(
          state.asset.type,
          [
            [EAssetType.CRYPTO, `Total Invested`],
            [EAssetType.EQUITY, `Total Invested`],
            [EAssetType.REAL_ESTATE, `Initial Property Value`],
            [EAssetType.COMMODITY, `Total Invested`],
            [EAssetType.ALTERNATIVE, `Total Invested`],
            [EAssetType.CASH, 'Cash Deposited']
          ],
          'Total Invested'
        )
        return {
          tooltip,
          label
        }
      }
    },
    fields: {
      value: defineField({
        type: 'number',
        value: 0,
        placeholder: '0',
        needs: ['asset.type', 'asset.source', 'providerImport', 'acquiredPrice'],
        next: (state) => {
          const providerImport = state.asset.providerImport as IProviderImport | null | undefined
          if (providerImport && state.acquiredPrice) {
            return {
              locked: true,
              ...lockedByProvider(state),
              value: state.invested.value || 0
            }
          }
          return {
            value: state.invested.value,
            ...(state.asset.source === EAssetSource.IR ? lockedByIr(state) : {})
          }
        }
      }),
      type: defineField({
        type: 'text',
        value: 'SEK',
        component: 'selectbox',
        injectedProps: {
          compact: true
        },
        options: {
          items: CURRENCIES
        },
        locked: true,
        needs: ['asset.type', 'asset.source', 'providerImport', 'acquiredPrice', 'isEditMode'],
        next: (state) => {
          const providerImport = state.asset.providerImport as IProviderImport | null | undefined
          if (providerImport) {
            return {
              locked: true,
              ...lockedByProvider(state),
              value: state.invested.type || settings.currency.value
            }
          }
          return {
            value: state.invested.type,
            locked: state.asset.source === EAssetSource.IR,
            ...(state.asset.source === EAssetSource.IR ? lockedByIr(state) : {})
          }
        }
      }),
      time: defineField({
        type: 'date',
        hidden: true,
        hooks: {
          'time': {
            fun: (state) => {
              return {
                value: state.time
              }
            }
          }
        },
        needs: ['asset.type', 'time'],
        next: (state) => ({
          value: state.time
        })
      })
    }
  })
}
